// The current month's forecast (SCR-011, FLOW-006, API-033): month to date, run-rate and projected
// bill per billed customer, with how far through the month it is.
import type { ApiError } from "@nexus/ui";
import { useCustomerDirectory } from "../../ui/customer-directory";
import { customerName } from "../../ui/customers";
import { Card, NameCell } from "@nexus/ui";
import { DataTable, Money, type Column } from "@nexus/ui";
import { forecastRows, forecastTitle, type ForecastRowView, type RevenueForecast } from "./model";

export function ForecastPanel({
  forecast,
  loading,
  error,
  onRetry,
  canOpenCustomers,
}: {
  forecast: RevenueForecast | null;
  loading: boolean;
  error: ApiError | null;
  onRetry: () => void;
  canOpenCustomers: boolean;
}) {
  const { entries } = useCustomerDirectory();
  const rows = forecast ? forecastRows(forecast) : [];
  const columns: Column<ForecastRowView>[] = [
    {
      key: "customer",
      header: "Customer",
      cell: (row) => {
        const name = customerName(entries, row.orgId);
        return canOpenCustomers ? <NameCell name={name} id={row.orgId} href={`/customers/${encodeURIComponent(row.orgId)}/billing`} /> : name;
      },
      compare: (a, b) => customerName(entries, a.orgId).localeCompare(customerName(entries, b.orgId)),
    },
    { key: "mtd", header: "Month to date", numeric: true, cell: (row) => <Money micro={row.monthToDatePriceMicroUsd} />, compare: (a, b) => a.monthToDatePriceMicroUsd - b.monthToDatePriceMicroUsd },
    { key: "runRate", header: "Run-rate a day", numeric: true, help: "finance.runRate", cell: (row) => <Money micro={row.runRateMicroUsdPerDay} />, compare: (a, b) => a.runRateMicroUsdPerDay - b.runRateMicroUsdPerDay },
    { key: "projected", header: "Projected", numeric: true, help: "finance.projected", cell: (row) => <Money micro={row.projectedPriceMicroUsd} />, compare: (a, b) => a.projectedPriceMicroUsd - b.projectedPriceMicroUsd },
    { key: "progress", header: "Days", numeric: true, cell: (row) => `${row.daysElapsed} of ${row.daysInMonth} (${row.progressPct}%)` },
  ];
  return (
    <Card title={forecast ? forecastTitle(forecast.period) : "Forecast"} titleId="forecast-heading" help="finance.forecast">
      {forecast && <p class="card-note">As of {new Date(forecast.asOf).toLocaleString()}. Late usage is not projected.</p>}
      <DataTable
        caption="Month to date, run-rate and projected bill per customer"
        hideCaption
        columns={columns}
        rows={rows}
        rowKey={(row) => row.orgId}
        loading={loading}
        error={error}
        onRetry={onRetry}
        emptyMessage="No billed customers to forecast this month."
      />
    </Card>
  );
}
